import { useCallback, useEffect, useRef, useState } from 'react';

import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';
import Text from '@cartwhl/ui/base/TextNew';

import type { MarketingMedia as Media } from './marketing-media';

interface MarketingMediaProps {
  className: string;
  dataPart?: string;
  eager?: boolean;
  media: Media;
  retainAfterLoaded?: boolean;
  /** null delays mounting until a client-only start time has been chosen. */
  startTime?: number | null;
}

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';
const maxAutomaticRetries = 4;
const retryDelayMs = 500;

function prefersStillMedia() {
  return (
    window.matchMedia(reducedMotionQuery).matches ||
    (navigator as Navigator & { connection?: { saveData?: boolean } })
      .connection?.saveData === true
  );
}

export default function MarketingMedia({
  className,
  dataPart,
  eager = false,
  media,
  retainAfterLoaded = true,
  startTime,
}: MarketingMediaProps) {
  if (media.type === 'empty') {
    return (
      <div
        className={`bg-tertiary ${className}`}
        role="img"
        aria-label={media.description}
        data-model-hero-part={dataPart}
      />
    );
  }

  if (media.type === 'image') {
    return (
      <div
        className={`bg-tertiary overflow-hidden ${className}`}
        data-model-hero-part={dataPart}
      >
        <img
          alt={media.description}
          className="h-full w-full object-cover"
          decoding="async"
          loading={eager ? 'eager' : 'lazy'}
          src={media.src}
        />
      </div>
    );
  }

  return (
    <MarketingVideo
      key={media.src}
      className={className}
      dataPart={dataPart}
      eager={eager}
      media={media}
      retainAfterLoaded={retainAfterLoaded}
      startTime={startTime}
    />
  );
}

interface MarketingVideoProps extends Omit<MarketingMediaProps, 'media'> {
  media: Extract<Media, { type: 'video' }>;
}

function MarketingVideo({
  className,
  dataPart,
  eager = false,
  media,
  retainAfterLoaded = true,
  startTime,
}: MarketingVideoProps) {
  const [stillPreferred, setStillPreferred] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [manualPlay, setManualPlay] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [retry, setRetry] = useState({ key: 0, failures: 0 });
  const [retriesExhausted, setRetriesExhausted] = useState(false);
  const [vp9Only, setVp9Only] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failed = useRef(false);
  const vp9Fallback = useRef(false);

  const cancelRetry = useCallback(() => {
    if (retryTimer.current !== null) clearTimeout(retryTimer.current);
    retryTimer.current = null;
  }, []);

  useEffect(() => cancelRetry, [cancelRetry]);

  useEffect(() => {
    failed.current = false;
  }, [useFallback]);

  const handleError = (details?: { code?: number; currentSrc: string }) => {
    if (failed.current) return;
    failed.current = true;
    setHasPlayed(false);
    if (media.fallbackSrc && !useFallback) {
      setUseFallback(true);
      setRetry(({ key }) => ({ key: key + 1, failures: 0 }));
      return;
    }
    const av1 = media.sources?.[0];
    if (
      details?.code === 3 &&
      av1?.type.includes('av01') &&
      details.currentSrc === new URL(av1.src, document.baseURI).href &&
      media.sources?.some((source) => source.type.includes('vp9'))
    ) {
      vp9Fallback.current = true;
    }
    if (retry.failures >= maxAutomaticRetries) {
      setRetriesExhausted(true);
      return;
    }
    retryTimer.current = setTimeout(
      () => {
        retryTimer.current = null;
        failed.current = false;
        setVp9Only(vp9Fallback.current);
        setRetry(({ key, failures }) => ({
          key: key + 1,
          failures: failures + 1,
        }));
      },
      retryDelayMs * (retry.failures + 1),
    );
  };

  const revealFrame = () => {
    if (!failed.current) setHasPlayed(true);
  };

  useEffect(() => {
    const query = window.matchMedia(reducedMotionQuery);
    const updatePreference = () => {
      const still = prefersStillMedia();
      setStillPreferred(still);
      setManualPlay(false);
      if (still) {
        cancelRetry();
        setHasPlayed(false);
        if (failed.current) {
          failed.current = false;
          setVp9Only(vp9Fallback.current);
          setRetriesExhausted(false);
          setRetry(({ key }) => ({ key: key + 1, failures: 0 }));
        }
      }
      setPreferencesReady(true);
    };
    updatePreference();
    query.addEventListener('change', updatePreference);
    return () => query.removeEventListener('change', updatePreference);
  }, [cancelRetry]);

  return (
    <div
      className={`bg-tertiary relative overflow-hidden ${className}`}
      data-model-hero-part={dataPart}
    >
      <div
        className="bg-tertiary absolute inset-0"
        role="img"
        aria-label={media.description}
      />
      {preferencesReady &&
        startTime !== null &&
        !retriesExhausted &&
        (!stillPreferred || manualPlay) && (
          <LazyVideo
            key={retry.key}
            src={useFallback ? media.fallbackSrc! : media.src}
            sources={
              useFallback
                ? undefined
                : vp9Only
                  ? media.sources?.filter((source) =>
                      source.type.includes('vp9'),
                    )
                  : media.sources
            }
            dimOnError={false}
            className={`absolute inset-0 h-full w-full rounded-[inherit] ${hasPlayed ? '' : 'opacity-0'}`}
            eager={eager}
            retainAfterLoaded={retainAfterLoaded}
            startTime={startTime}
            rootMargin="200px"
            threshold={0}
            inViewThreshold={0.001}
            strictViewportPlayback
            preload={eager ? 'auto' : 'metadata'}
            onFirstFrame={revealFrame}
            onLoadedData={revealFrame}
            onInViewChange={(inView) => {
              if (!inView && !retainAfterLoaded) setHasPlayed(false);
            }}
            onError={handleError}
          />
        )}
      {preferencesReady &&
        retriesExhausted &&
        (!stillPreferred || manualPlay) && (
          <button
            type="button"
            className="bg-primary text-primary absolute bottom-4 right-4 z-20 rounded-lg px-4 py-2"
            onClick={() => {
              cancelRetry();
              failed.current = false;
              setVp9Only(vp9Fallback.current);
              setRetriesExhausted(false);
              setHasPlayed(false);
              setRetry(({ key }) => ({ key: key + 1, failures: 0 }));
            }}
            aria-label={`Retry ${media.description}`}
          >
            <Text as="span" token="14/medium">
              Retry video
            </Text>
          </button>
        )}
      {preferencesReady && stillPreferred && (
        <button
          type="button"
          className="bg-primary text-primary absolute bottom-4 left-4 z-20 rounded-lg px-4 py-2"
          onClick={() => {
            cancelRetry();
            failed.current = false;
            setVp9Only(vp9Fallback.current);
            setRetriesExhausted(false);
            setRetry(({ key }) => ({ key: key + 1, failures: 0 }));
            setManualPlay((playing) => !playing);
            setHasPlayed(false);
          }}
          aria-label={`${manualPlay ? 'Stop' : 'Play'} ${media.description}`}
        >
          <Text as="span" token="14/medium">
            {manualPlay ? 'Stop video' : 'Play video'}
          </Text>
        </button>
      )}
    </div>
  );
}
